import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyStore } from '../../../stores/companyStore';
import { notify } from '../../../stores/notificationStore';
import {
  isVatExempt,
  isIntraCommunity,
  resolveDocumentVatExemptArticle,
  formatDate,
} from '../../../utils/vatCalculations';
import { QuoteTemplateProps, QuoteItem } from '../types';
import { ActionBar } from '../shared/ActionBar';
import { ClientInfoSection } from '../shared/ClientInfoSection';
import { ItemsTable } from '../shared/ItemsTable';
import { TotalsSection } from '../shared/TotalsSection';
import { LegalFooter } from '../shared/LegalFooter';
import { UnifiedEmailModal, EmailData } from '../../modals/UnifiedEmailModal';
import { publicLinksAPI, quotesAPI } from '../../../utils/api';
import { logger } from '../../../utils/logger';

/**
 * Template Classique (FREE) - DEVIS
 * - Logo: top-left
 * - Header: standard
 * - Table: striped
 */
export const QuoteTemplateClassic: React.FC<QuoteTemplateProps> = ({
  quoteData,
  onClose,
  template,
  company: companyProp,
  color: colorProp
}) => {
  const { t } = useTranslation();
  const { company: companyStore } = useCompanyStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [publicLink, setPublicLink] = useState<string>('');

  const company = companyProp || companyStore;
  const quoteColor = colorProp || template?.primaryColor || '#0D9488';

  const clientCountryCode = (quoteData as any)?.Client?.countryCode;
  const isExempt = isVatExempt(company?.vatRegime) || isIntraCommunity(clientCountryCode, parseFloat(String(quoteData?.totalTVA)) || undefined);
  const vatExemptArticle = resolveDocumentVatExemptArticle(company?.vatRegime, clientCountryCode, parseFloat(String(quoteData?.totalTVA)) || undefined);

  if (!quoteData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <p className="text-red-600 font-semibold mb-4">{t('templates.quote.noData')}</p>
          <button onClick={onClose} className="px-4 py-2 bg-gradient-to-r from-teal-600 to-purple-600 text-white rounded-lg">
            {t('templates.common.back')}
          </button>
        </div>
      </div>
    );
  }

  // Normaliser les items : spread pour garder tous les champs (lineType, unit, descriptionType, technicalDescription...)
  // + override des numériques (Prisma Decimal → number, totalHT → total)
  const items: QuoteItem[] = typeof quoteData.items === 'string'
    ? JSON.parse(quoteData.items).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),
          vatAmount: item.vatAmount || 0,
        };
      })
    : (quoteData.items || []).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),
          vatAmount: item.vatAmount || 0,
        };
      });

  const discountType = quoteData.discountType || 'none';
  const discountValue = parseFloat(String(quoteData.discountValue)) || 0;
  const discountAmount = parseFloat(String(quoteData.discountAmount)) || 0;

  // ✅ Construire la ventilation TVA à partir des items (affichage uniquement)
  const vatBreakdown: Array<{ rate: number; baseHT: number; vatAmount: number }> = [];
  const vatByRate: Record<number, { baseHT: number; vatAmount: number }> = {};

  items.forEach((item: QuoteItem) => {
    const rate = item.taxPercent || 0;
    if (!vatByRate[rate]) {
      vatByRate[rate] = { baseHT: 0, vatAmount: 0 };
    }
    vatByRate[rate].baseHT += item.total || 0;
    const vatAmt = (item.total || 0) * (rate / 100);
    vatByRate[rate].vatAmount += vatAmt;
  });

  Object.entries(vatByRate).forEach(([rate, data]) => {
    vatBreakdown.push({
      rate: Number(rate),
      baseHT: data.baseHT,
      vatAmount: data.vatAmount
    });
  });

  // ✅ Utiliser les valeurs du backend (source de vérité)
  const subtotal = parseFloat(String(quoteData.totalHT)) || parseFloat(String(quoteData.subtotal)) || 0;
  const tax = parseFloat(String(quoteData.totalTVA)) || parseFloat(String(quoteData.tax)) || 0;
  const total = parseFloat(String(quoteData.totalTTC)) || parseFloat(String(quoteData.total)) || 0;

  const handleGeneratePDF = async () => {
    if (!quoteData.id) {
      notify.error('ID du devis manquant', 'Génération PDF');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF (templates uniformisés)
      const blob = await quotesAPI.downloadPDF(quoteData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (quoteData as any).Client?.fullName || (quoteData as any).Client?.companyName || 'Client';
      const quoteNumber = (quoteData.quoteNumber || 'Devis').replace(/^DEVIS-/i, '');
      a.download = `Devis_${quoteNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF généré avec succès', 'Génération PDF');
    } catch (error) {
      logger.error('Erreur génération PDF:', error);
      notify.error("Erreur lors de la génération du PDF : " + (error as Error).message, 'Génération PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenEmailModal = async () => {
    try {
      setIsLoading(true);
      if (!quoteData.id) {
        throw new Error('ID du devis manquant');
      }

      // 1. Essayer de récupérer un lien existant
      try {
        const result = await publicLinksAPI.getQuoteLink(quoteData.id);
        if (result.success && result.data && result.data.length > 0) {
          const link = result.data[0];
          setPublicLink(`${window.location.origin}/q/${link.token}`);
          return;
        }
      } catch {
        // 404 ou erreur réseau — on continue pour créer un lien
      }

      // 2. Pas de lien existant, en créer un nouveau
      const createResult = await publicLinksAPI.createQuoteLink(quoteData.id);
      if (createResult.success && createResult.data && createResult.data.token) {
        setPublicLink(`${window.location.origin}/q/${createResult.data.token}`);
      }
    } catch (error) {
      logger.error('Erreur lors de la récupération du lien public:', error);
    } finally {
      setIsLoading(false);
      setShowEmailModal(true);
    }
  };

  const handleSendEmail = async (emailData: EmailData) => {
    try {
      if (!quoteData?.id) {
        throw new Error('ID du devis manquant');
      }

      setIsLoading(true);

      // Envoyer en JSON — le backend génère le PDF côté serveur
      await publicLinksAPI.sendQuoteEmail(quoteData.id, {
        to: emailData.to,
        subject: emailData.subject,
        body: emailData.body,
      });

      notify.success(`Email envoyé à ${emailData.to}`, 'Envoi réussi');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      notify.error(message, 'Erreur d\'envoi');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen print:min-h-0 print:bg-white">
      <ActionBar
        onGeneratePDF={handleGeneratePDF}
        onGenerateFacturX={handleGeneratePDF} // Même action que PDF pour devis
        onSendEmail={handleOpenEmailModal}
        onPrint={handlePrint}
        onClose={onClose}
        isLoading={isLoading}
        documentType="quote"
      />

      <UnifiedEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        documentType="quote"
        documentNumber={quoteData.quoteNumber}
        clientEmail={quoteData.clientEmail || ''}
        clientName={quoteData.clientName || ''}
        showPdfPreview={false}
        templateVariables={{
          quoteNumber: quoteData.quoteNumber || '',
          clientName: quoteData.clientName || '',
          total: total.toFixed(2),
          publicLink: publicLink,
          validUntil: formatDate(quoteData.validUntil),
          companyName: company?.legalName || company?.tradeName || '',
          companyEmail: company?.email || '',
          companyPhone: company?.phone || '',
        }}
      />

      <div className="py-8 print:py-0 print:mt-0">
        <div className="max-w-4xl mx-auto bg-white shadow-2xl print:shadow-none print:max-w-full" id="quote-template">
          <div className="p-4 md:p-8">
            {/* Header avec ou sans logo */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-8 md:mb-12 gap-6">
              {/* Company Info */}
              <div>
                {/* Logo si disponible */}
                {company?.logoUrl && (
                  <div className="mb-1 md:mb-2">
                    <img
                      src={company.logoUrl}
                      alt={company.legalName || company.tradeName || 'Logo'}
                      className="h-16 md:h-20 max-w-[200px] object-contain"
                    />
                  </div>
                )}

                {/* Nom de l'entreprise - grand si pas de logo, normal si logo */}
                {!company?.logoUrl && (
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    {company?.legalName || company?.tradeName}
                  </h2>
                )}

                <div className="text-gray-600 mt-1 md:mt-2 space-y-0.5 md:space-y-1 text-xs md:text-sm">
                  {/* Nom en taille normale si logo présent */}
                  {company?.logoUrl && (
                    <p className="font-bold text-gray-800">
                      {company?.legalName || company?.tradeName}
                    </p>
                  )}

                  {company?.addressLine1 && (
                    <p>{company.addressLine1}</p>
                  )}
                  {((company?.zipCode) || (company?.city)) && (
                    <p>
                      {(company?.zipCode) && `${company.zipCode} - `}
                      {company?.city}
                    </p>
                  )}
                  {company?.phone && (
                    <p>{company.phone}</p>
                  )}
                  {company?.email && (
                    <p style={{ color: quoteColor }}>{company.email}</p>
                  )}
                </div>
              </div>

              <div className="md:text-right">
                <div
                  className="text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg mb-3 md:mb-4 flex items-center gap-2 justify-end"
                  style={{ backgroundColor: quoteColor }}
                >
                  <h3 className="text-xs md:text-base font-bold">{t('templates.quote.title')}</h3>
                  <span className="text-xs md:text-sm font-semibold">
                    {quoteData.quoteNumber?.replace(/^DEVIS-/i, '') || quoteData.quoteNumber}
                  </span>
                </div>

                <div className="text-gray-600 space-y-1 md:space-y-2 text-xs md:text-sm">
                  <div>
                    <span className="font-medium">{t('templates.quote.dateLabel')} </span>
                    <span>{formatDate(quoteData.date)}</span>
                  </div>
                  <div>
                    <span className="font-medium">{t('templates.quote.validUntilLabel')} </span>
                    <span>{formatDate(quoteData.validUntil)}</span>
                  </div>

                  <ClientInfoSection data={quoteData} accentColor={quoteColor} />
                </div>
              </div>
            </div>

            {/* Titre du document */}
            {quoteData.title && (
              <div
                className="mb-6 text-2xl font-semibold border-l-4 pl-4 py-2 text-gray-800"
                style={{ borderColor: quoteColor }}
              >
                {quoteData.title}
              </div>
            )}

            {/* Items Table - Style STRIPED */}
            <ItemsTable
              items={items}
              tableStyle="striped"
              accentColor={quoteColor}
              showVat={!isExempt}
            />

            {/* Totals Section */}
            <TotalsSection
              subtotal={subtotal}
              tax={tax}
              total={total}
              discount={discountAmount}
              discountType={discountType}
              discountValue={discountValue}
              vatBreakdown={vatBreakdown}
              accentColor={quoteColor}
              showVatBreakdown={!isExempt}
              isVatExempt={isExempt}
              vatExemptionMention={vatExemptArticle}
            />

            {/* Legal Footer */}
            <LegalFooter company={company} notes={quoteData.termsConditions} vatExemptArticle={vatExemptArticle} />
          </div>
        </div>
      </div>
    </div>
  );
};
